import { Children, useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type {
  IDropdownMenuInternalProps,
  IDropdownMenu,
  IAccountDropdownMenu,
  ITextFieldDropdownMenu,
  IPhoneFieldDropdownMenu,
  ICheckboxesDropdownMenu,
  IDefaultDropdownMenu
} from '../../../types/Dropdown/dropdown_menu/dropdown-menu';
import type { IInput } from 'libs/design-system/src/types/Input/input';
import {
  getDir,
  getHorizontalPlacement,
  getVerticalPlacement
} from './../../../utils/getPlacement';
import { cloneNestedComponent } from './../../../utils/cloneNestedComponent';
import { DropdownMenuItem } from '../DropdownMenuItem';
import { Account } from './Account';
import { TextField } from './TextField';
import { PhoneField } from './PhoneField';
import { Checkboxes } from './Checkboxes';
import { Input } from '../../Input';
import styles from './dropdown-menu.module.scss';

export const DropdownMenu = (props: IDropdownMenu) => {

  const { closeDropdown, isOpen = false, dropdownRef } = (props as IDropdownMenuInternalProps);
  const { searchable, searchInputProps } = props as IDefaultDropdownMenu;
  const {
    children,
    open,
    type = 'default',
    horizontalPlacement = 'start',
    verticalPlacement = 'bottom',
    width = 'full',
    maxHeight = '20rem',
    onToggleHandler
  } = props;

  const menuRef = useRef<HTMLDivElement>(null);
  // Optional callback provided by parent to register the actual menu DOM node
  // (useful when the menu is portaled to document.body so parent can include
  // the menu in click-outside detection).
  const { registerMenuRef } = props as any;
  const [fixedPosition, setFixedPosition] = useState<{ top: number; left: number } | null>(null);
  const [isPositioned, setIsPositioned] = useState<boolean>(false);

  const calculateVerticalPosition = useCallback((rect: DOMRect, menuRect: DOMRect, effectiveVertical: string, isInlineHorizontal: boolean) => {
    const triggerGap = rect.height * 0.1;
    if (effectiveVertical === 'top') {
      return isInlineHorizontal ? (rect.bottom - menuRect.height) : (rect.top - menuRect.height - triggerGap);
    }
    return isInlineHorizontal ? rect.top : (rect.bottom + triggerGap);
  }, []);

  const calculateHorizontalPosition = useCallback((rect: DOMRect, menuRect: DOMRect, effectivePlacement: string, dir: string) => {
    if (effectivePlacement === 'start') {
      return dir === 'ltr' ? rect.left : rect.right - menuRect.width;
    } else if (effectivePlacement === 'end') {
      return dir === 'ltr' ? rect.right - menuRect.width : rect.left;
    } else if (effectivePlacement === 'center') {
      return rect.left + (rect.width - menuRect.width) / 2;
    } else if (effectivePlacement === 'inline_start') {
      const triggerOffset = rect.width * 0.05;
      return dir === 'ltr' ? rect.left - menuRect.width - triggerOffset : rect.right + triggerOffset;
    } else if (effectivePlacement === 'inline_end') {
      const triggerOffset = rect.width * 0.05;
      return dir === 'ltr' ? rect.right + triggerOffset : rect.left - menuRect.width - triggerOffset;
    }
    return dir === 'ltr' ? rect.left : rect.right - menuRect.width;
  }, []);

  const getEffectivePlacements = useCallback((dir: string) => {
    if (!dropdownRef) return { effectiveHorizontal: horizontalPlacement, effectiveVertical: verticalPlacement };
    
    const autoHor = getHorizontalPlacement(dropdownRef, dir as 'ltr' | 'rtl');
    const autoVert = getVerticalPlacement(dropdownRef);

    const effectiveHorizontal = horizontalPlacement === 'auto'
      ? (autoHor ?? 'start')
      : horizontalPlacement === 'inline_auto'
        ? (autoHor ? `inline_${autoHor === 'start' ? 'end' : 'start'}` : 'inline_start')
        : horizontalPlacement;

    const effectiveVertical = verticalPlacement === 'auto' ? (autoVert ?? 'bottom') : verticalPlacement;

    return { effectiveHorizontal, effectiveVertical };
  }, [horizontalPlacement, verticalPlacement, dropdownRef]);

  const computePosition = useCallback(() => {
    if (!dropdownRef?.current || !menuRef?.current) return;

    const rect = dropdownRef.current.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();
    const dir = getDir();
    const { effectiveHorizontal, effectiveVertical } = getEffectivePlacements(dir);

    const isInlineHorizontal = String(effectiveHorizontal).startsWith('inline_');
    const top = calculateVerticalPosition(rect, menuRect, effectiveVertical, isInlineHorizontal);
    const left = calculateHorizontalPosition(rect, menuRect, effectiveHorizontal, dir);

    setFixedPosition({ top: Math.max(0, top), left: Math.max(0, left) });
  }, [dropdownRef, calculateVerticalPosition, calculateHorizontalPosition, getEffectivePlacements]);

  const setupEventListeners = useCallback((callback: () => void) => {
    let scheduled = false;
    const onScrollOrResize = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        callback();
      });
    };

    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);

    let ancestor: HTMLElement | null = dropdownRef?.current?.parentElement || null;
    while (ancestor) {
      const overflowY = window.getComputedStyle(ancestor).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        ancestor.addEventListener('scroll', onScrollOrResize, { passive: true });
        break;
      }
      ancestor = ancestor.parentElement;
    }

    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      if (ancestor) ancestor.removeEventListener('scroll', onScrollOrResize);
    };
  }, [dropdownRef]);

  useEffect(() => {
    if (open ?? isOpen) {
      setIsPositioned(false);
      const raf = requestAnimationFrame(() => {
        computePosition();
        setTimeout(() => setIsPositioned(true), 20);
      });

      const cleanup = setupEventListeners(computePosition);
      // Inform parent about the mounted menu element so clicks inside the
      // portaled menu aren't treated as outside clicks.
      registerMenuRef?.(menuRef.current || null);
      return () => {
        cancelAnimationFrame(raf);
        cleanup();
        registerMenuRef?.(null);
      };
    } else {
      setIsPositioned(false);
      setFixedPosition(null);
    }
  }, [open ?? isOpen, computePosition, setupEventListeners]);

  // Keep the parent informed of open/close state. Declared up here with the
  // other hooks so the early return below can't change hook order.
  useEffect(() => {
    onToggleHandler?.(open ?? isOpen);
  }, [open, isOpen]);

  // Bail out before doing any layout-reading work when the menu is closed.
  // The width and auto-placement calculations below all call
  // getBoundingClientRect, which forces a synchronous reflow. Running them on
  // every render of every *closed* dropdown (their results are only used in the
  // open-only menu JSX) was a measurable source of forced reflow during the
  // initial render. Closed menus now do zero layout reads.
  if (!(open ?? isOpen)) return <> </>;

  // Calculate width style
  let widthStyle: string | undefined;
  if (width === 'full') {
    widthStyle = dropdownRef?.current?.getBoundingClientRect().width + 'px';
  } else if (width === 'fit-content') {
    widthStyle = 'fit-content';
  } else {
    widthStyle = width;
  }

  let content = <></>;
  let additionalContent = <></>;

  if (type === 'account') {
    content = <Account {...props as IAccountDropdownMenu} />;
  } else if (type === 'text_field') {
    content = <TextField {...props as ITextFieldDropdownMenu & IDropdownMenuInternalProps} />;
  } else if (type === 'phone_field') {
    content = <PhoneField {...props as IPhoneFieldDropdownMenu & IDropdownMenuInternalProps} />;
  } else if (type === 'checkboxes') {
    additionalContent = <Checkboxes {...props as ICheckboxesDropdownMenu & IDropdownMenuInternalProps} />;
  };

  const autoHorizontalPlacement = typeof dropdownRef?.current === 'object' && getHorizontalPlacement(dropdownRef, getDir());
  const autoVerticalPlacement = typeof dropdownRef?.current === 'object' && getVerticalPlacement(dropdownRef);

  let horizontal_placement: IDropdownMenu['horizontalPlacement'] = horizontalPlacement;
  let vertical_placement: IDropdownMenu['verticalPlacement'] = verticalPlacement;

  if (horizontalPlacement === 'auto') {
    horizontal_placement = autoHorizontalPlacement ? autoHorizontalPlacement : 'start';
  } else if (horizontalPlacement === 'inline_auto') {
    const auto_horizontal_placement = autoHorizontalPlacement === "start" ? "end" : "start";
    horizontal_placement = autoHorizontalPlacement ? `inline_${auto_horizontal_placement}` : 'inline_start';
  };

  if (vertical_placement === 'auto') {
    vertical_placement = autoVerticalPlacement ? autoVerticalPlacement : 'bottom';
  };

  // Render the positioned menu into document.body to avoid clipping and stacking
  // context issues on iOS browsers. Use a portal so `position: fixed` is truly
  // relative to the viewport and not accidentally bounded by transformed/stacked
  // ancestors. (Closed-state early return happens above, before layout reads.)
  const menu = (
    <div
      ref={menuRef}
      className={`${styles['dropdown-menu']} ${isPositioned ? styles['dropdown-menu--open'] : ''}`}
      data-dropdown-menu-horizontal={horizontal_placement}
      data-dropdown-menu-vertical={vertical_placement}
      data-dropdown-menu-width={width}
      style={{
        maxHeight: maxHeight,
        overflowY: maxHeight === 'fit-content' ? 'visible' : 'auto',
        width: widthStyle,
        visibility: isPositioned ? 'visible' : 'hidden',
        pointerEvents: isPositioned ? 'auto' : 'none',
        ...(fixedPosition && { top: `${fixedPosition.top}px`, left: `${fixedPosition.left}px` })
      }}
    >
      {
        type === 'default' && searchable &&
        <div className={styles['dropdown-menu__search-bar']}>
          <Input {...(searchInputProps || {}) as IInput} />
        </div>
      }

      {content}

      {
        type !== 'text_field' &&
        Children.map(children, (child: any) => {
          const childDropdownMenuItem = cloneNestedComponent(child, DropdownMenuItem, {
            closeDropdown: closeDropdown
          });

          return childDropdownMenuItem ? childDropdownMenuItem : child
        })
      }

      {additionalContent}

    </div>
  );

  // Choose a portal container:
  // - If the dropdown trigger lives inside a <dialog>, portal into that dialog
  //   so the menu renders above the dialog backdrop.
  // - Otherwise portal into document.body as before.
  if (typeof document === 'undefined') return menu;

  let portalContainer: HTMLElement | null = null;
  try {
    const triggerEl = dropdownRef?.current as HTMLElement | null;
    portalContainer = triggerEl?.closest && (triggerEl.closest('dialog') as HTMLElement | null) || document.body;
  } catch (e) {
    portalContainer = document.body;
  }

  return createPortal(menu, portalContainer);
};